import { blockFacetten, type Facette, type Hoehenfunktion } from './gelaende-block';

/**
 * Schraegbilder zum Hanganschluss: Gehoert der Hang zu einem Voll- oder zu
 * einem Hohlrelief? Gefragt wird das nur bei staerker geneigten Haengen
 * (ueber 3 Grad).
 *
 * ⚠ Woher die Formen kommen: aus den beiden Spalten „Oben“ und „Unten“
 * derselben Tabelle. Der Hang selbst - hinten hoch, vorn tief - ist in allen
 * vier Bildern dieselbe Kurve; dazu kommt oben eine Kuppe und/oder unten eine
 * Senke:
 *   Oben  „Ebene“ = eine flache Hochflaeche, „Kuppe“ = ein runder Gipfel, der
 *                   nach hinten und zur Seite wieder abfaellt,
 *   Unten „Ebene“ = eine flache Tieflaeche, „Senke“ = eine Hohlform, die nach
 *                   vorn und zur Seite wieder ansteigt.
 * Es ist eine Zeichnung dieser Saetze, keine Vermessung.
 *
 * Gezeichnet wird mit dem gemeinsamen Gelaendeblock (`gelaende-block.ts`) -
 * derselbe Blickwinkel und derselbe Bodensockel wie bei Woelbung, Neigung und
 * Einbindung, damit die Bilder untereinander vergleichbar bleiben.
 */

/** Was oberhalb des Hanges anschliesst. */
export type ObererAnschluss = 'Ebene' | 'Kuppe';
/** Was unterhalb des Hanges anschliesst. */
export type UntererAnschluss = 'Ebene' | 'Senke';

/** Halbe Hoehenspanne des Hanges: von der oberen zur unteren Ebene sind es 2 x A. */
const A = 0.36;
/** Wie kurz der Hang ist. Groesser heisst: steiler in der Mitte, flacher an den Enden. */
const HANGKANTE = 2.8;
/** Wie hoch die Kuppe ueber die obere Ebene ragt. */
const KUPPE = 0.46;
/** Wie tief die Senke unter die untere Ebene reicht. */
const SENKE = 0.46;
/**
 * Wo Gipfel und Sohle liegen und wie eng sie gerundet sind.
 *
 * ⚠ Beide sitzen mit Absicht INNERHALB des Blocks und nicht an seiner Kante:
 * Nur so faellt die Kuppe im Bild nach hinten wieder ab und die Senke steigt
 * nach vorn wieder an - und erst daran sieht man, dass es ein Gipfel und eine
 * Hohlform ist und nicht einfach eine hoehere bzw. tiefere Ebene. Bei 56 Pixeln
 * Bildbreite entscheidet das darueber, ob sich die vier Lagen ueberhaupt
 * unterscheiden lassen (nebeneinander gerendert und verglichen, 19.09.2026).
 */
const GIPFEL = -0.58;
const SOHLE = 0.58;
const RUNDUNG = 0.36;
/** Wie stark Kuppe und Senke auch zur Seite hin auslaufen. */
const SEITLICH = 0.85;

/** Weiche Stufe: hinten oben, vorn unten, in der Mitte der Hang. */
function hang(laengs: number): number {
	return (-A * Math.tanh(laengs * HANGKANTE)) / Math.tanh(HANGKANTE);
}

/** Glockenkurve: bei 0 ganz, nach aussen abfallend. */
function glocke(t: number): number {
	return Math.exp(-t * t);
}

/** Der Gipfel oberhalb des Hanges - nach hinten und zur Seite faellt er ab. */
function kuppe(laengs: number, quer: number): number {
	return KUPPE * glocke((laengs - GIPFEL) / RUNDUNG) * (1 - SEITLICH * quer * quer);
}

/** Die Hohlform unterhalb des Hanges - nach vorn und zur Seite steigt sie an. */
function senke(laengs: number, quer: number): number {
	return -SENKE * glocke((laengs - SOHLE) / RUNDUNG) * (1 - SEITLICH * quer * quer);
}

/**
 * Hoehe an einer Stelle des Blocks.
 *
 * @param laengs Lage in Gefaellrichtung, -1 = bergauf/hinten, +1 = bergab/vorn
 * @param quer   Lage quer zum Hang, -1 bis +1
 */
export function hanganschlussHoehe(
	oben: ObererAnschluss,
	unten: UntererAnschluss,
	laengs: number,
	quer: number,
): number {
	return (
		hang(laengs) +
		(oben === 'Kuppe' ? kuppe(laengs, quer) : 0) +
		(unten === 'Senke' ? senke(laengs, quer) : 0)
	);
}

/**
 * Die vier Lagen.
 *
 * ⚠ Zweitschrift: Oben und Unten stehen auch in `assets/json/Hanganschluss.json`.
 * `relief-uebernahme.spec.ts` gleicht beide ab, damit das Bild nicht eines Tages
 * etwas anderes zeigt, als die Zeile sagt.
 */
export const HANGANSCHLUSS_LAGEN: { id: string; oben: ObererAnschluss; unten: UntererAnschluss }[] = [
	{ id: 'B-Lage', oben: 'Ebene', unten: 'Ebene' },
	{ id: 'V-Lage', oben: 'Kuppe', unten: 'Ebene' },
	{ id: 'H-Lage', oben: 'Ebene', unten: 'Senke' },
	{ id: 'D-Lage', oben: 'Kuppe', unten: 'Senke' },
];

/** Hoehenmodell zu einer Lage, oder `null` wenn die Lage unbekannt ist. */
export function hanganschlussForm(lage: string): Hoehenfunktion | null {
	const eintrag = HANGANSCHLUSS_LAGEN.find(l => l.id === lage);
	return eintrag ? (laengs, quer) => hanganschlussHoehe(eintrag.oben, eintrag.unten, laengs, quer) : null;
}

/** Die Facetten des Schraegbildes zu einer Lage. */
export function hanganschlussFacetten(lage: string): Facette[] {
	const form = hanganschlussForm(lage);
	return form ? blockFacetten(form) : [];
}
